import * as React from 'react'
import { Row } from 'reactstrap'
import { useFormContext, useWatch } from 'react-hook-form'
import { SubscriptionCreateFormInputs } from '@modules/subscription/create/modal'
import { ClientSelect } from '@components/client-select'
import { ClientUser } from '@models/clients'
import { useModal } from '@components/modals/use-modal'
import { InvoiceForm } from '@components/invoice-form'
import { createInvoiceData } from '@components/products/product-invoice-card'
import { SubscriptionDetails, SubscriptionType } from '@modules/subscription/models'
import { useAuthenticatedUser } from '@components/hooks/use-authenticated-user'
import { UserPermission } from '@models/dashboard'
import { SubscriptionCreateFormAdditionalClient } from '@modules/subscription/create/steps/additional-client'
import { useSubscriptionTypeStrategyProvider } from '@modules/subscription/hooks/use-subscription-type-provider'

interface Props {
  subscriptionDetails?: SubscriptionDetails
  type: SubscriptionType
  initialClient?: ClientUser | null
}

export const SubscriptionCreateFormClientData: React.FC<Props> = ({ initialClient, subscriptionDetails }) => {
  const [client, setClient] = React.useState<ClientUser | undefined>(initialClient ?? undefined)

  const { formState, setValue, clearErrors, control } = useFormContext<SubscriptionCreateFormInputs>()

  const clients = useWatch({ control, name: 'clients' })

  const [handleToggleClientDialog] = useModal('ClientDialog', { setClient })

  React.useEffect(() => {
    if (!client) return
    setValue('client', client)
    clearErrors()
  }, [client])

  const user = useAuthenticatedUser()

  const isClientEditDisabled =
    !!subscriptionDetails &&
    subscriptionDetails?.status !== 'initial' &&
    !user.hasPerm(UserPermission.SubscriptionCanChangeClient)

  const additionalClientsEmail = clients.map(client => client.email)

  return (
    <>
      <Row>
        <ClientSelect
          setClient={setClient}
          client={client}
          handleToggleClientDialog={handleToggleClientDialog}
          error={formState.errors.client}
          isDisabled={isClientEditDisabled}
          emailsToOmit={additionalClientsEmail.length ? additionalClientsEmail : []}
        />
        {subscriptionDetails && <AdditionalClientUpdate subscriptionDetails={subscriptionDetails} />}
      </Row>

      <InvoiceForm client={client} invoiceData={subscriptionDetails && createInvoiceData(subscriptionDetails)} />
    </>
  )
}

const AdditionalClientUpdate = ({ subscriptionDetails }) => {
  const { allow_add_additional_clients } = useSubscriptionTypeStrategyProvider(subscriptionDetails.type)

  if (!allow_add_additional_clients && !subscriptionDetails.product?.allow_add_second_client) {
    return null
  }

  return <SubscriptionCreateFormAdditionalClient subscriptionDetails={subscriptionDetails} />
}
